import React, { useState } from 'react';
import {
  Trash2,
  Terminal,
  Minimize2,
  Move,
  Activity,
  Maximize2,
} from 'lucide-react';

import useDebugStore from '../hooks/useDebugStore';
import { CONFIG } from '../constants/config';

/**
 * A premium Debug Overlay component that displays logs in the UI.
 */
const DebugOverlay = () => {
  const { logs, networkLogs, clearLogs } = useDebugStore();
  const [isExpanded, setIsExpanded] = useState(false);
  const [filter, setFilter] = useState('all');
  const [position, setPosition] = useState('bottom-right'); // bottom-right, bottom-left, top-right, top-left
  const [activeTab, setActiveTab] = useState('console'); // console, network
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [expandedNetworkLog, setExpandedNetworkLog] = useState(null);

  // If not in DEV/DEBUG mode, don't render anything
  if (!CONFIG.IS_DEV_OR_DEBUG) return null;

  const filteredLogs = logs.filter(
    (log) => filter === 'all' || log.type === filter,
  );

  const getLogColor = (type) => {
    switch (type) {
      case 'error':
        return '#ef4444';
      case 'warn':
        return '#f59e0b';
      case 'info':
        return '#10b981';
      default:
        return '#3b82f6';
    }
  };

  const cyclePosition = () => {
    const positions = ['bottom-right', 'bottom-left', 'top-left', 'top-right'];
    const nextIndex = (positions.indexOf(position) + 1) % positions.length;
    setPosition(positions[nextIndex]);
  };

  const getPositionStyles = () => {
    if (isFullScreen) return {};
    switch (position) {
      case 'bottom-left':
        return { bottom: '20px', left: '20px' };
      case 'top-left':
        return { top: '20px', left: '20px' };
      case 'top-right':
        return { top: '20px', right: '20px' };
      default:
        return { bottom: '20px', right: '20px' };
    }
  };

  return (
    <div
      className="debug-overlay-container"
      style={{
        position: 'fixed',
        ...(isFullScreen
          ? { top: '20px', left: '20px', right: '20px', bottom: '20px' }
          : getPositionStyles()),
        zIndex: 9999,
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: '12px',
        pointerEvents: 'auto',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Floating Toggle Button */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          style={{
            background: 'rgba(24, 24, 27, 0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(63, 63, 70, 0.5)',
            borderRadius: '12px',
            padding: '10px 16px',
            color: '#e4e4e7',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.transform = 'translateY(-2px)')
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.transform = 'translateY(0)')
          }
        >
          <Terminal size={16} />
          <span>Debug Console ({logs.length})</span>
        </button>
      )}

      {/* Expanded Console */}
      {isExpanded && (
        <div
          style={{
            width: isFullScreen ? '100%' : '500px',
            height: isFullScreen ? '100%' : '400px',
            background: 'rgba(24, 24, 27, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(63, 63, 70, 0.8)',
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            overflow: 'hidden',
            animation: 'slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid rgba(63, 63, 70, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'rgba(39, 39, 42, 0.5)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                color: '#e4e4e7',
              }}
            >
              <button
                type="button"
                onClick={() => setActiveTab('console')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  opacity: activeTab === 'console' ? 1 : 0.5,
                  transition: 'opacity 0.2s',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: 'inherit',
                  font: 'inherit',
                }}
              >
                <Terminal size={14} />
                <span style={{ fontWeight: 600 }}>Console</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('network')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  opacity: activeTab === 'network' ? 1 : 0.5,
                  transition: 'opacity 0.2s',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: 'inherit',
                  font: 'inherit',
                }}
              >
                <Activity size={14} />
                <span style={{ fontWeight: 600 }}>Network</span>
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={cyclePosition}
                title="Move Position"
                style={iconButtonStyle}
              >
                <Move size={14} />
              </button>
              <button
                onClick={clearLogs}
                title="Clear Logs"
                style={iconButtonStyle}
              >
                <Trash2 size={14} />
              </button>
              <button
                onClick={() => setIsFullScreen(!isFullScreen)}
                title={isFullScreen ? 'Exit Full Screen' : 'Full Screen'}
                style={{
                  ...iconButtonStyle,
                  color: isFullScreen ? '#60a5fa' : '#a1a1aa',
                }}
              >
                <Maximize2 size={14} />
              </button>
              <button
                onClick={() => setIsExpanded(false)}
                title="Minimize"
                style={iconButtonStyle}
              >
                <Minimize2 size={14} />
              </button>
            </div>
          </div>

          {activeTab === 'console' ? (
            <>
              {/* Filters */}
              <div
                style={{
                  padding: '8px 16px',
                  display: 'flex',
                  gap: '8px',
                  background: 'rgba(24, 24, 27, 0.5)',
                  borderBottom: '1px solid rgba(63, 63, 70, 0.3)',
                }}
              >
                {['all', 'log', 'info', 'warn', 'error'].map((t) => (
                  <button
                    key={t}
                    onClick={() => setFilter(t)}
                    style={{
                      padding: '2px 8px',
                      borderRadius: '4px',
                      border: 'none',
                      background:
                        filter === t
                          ? 'rgba(59, 130, 246, 0.2)'
                          : 'transparent',
                      color: filter === t ? '#60a5fa' : '#a1a1aa',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>

              {/* Console Log List */}
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  padding: '8px 0',
                }}
              >
                {filteredLogs.length === 0 ? (
                  <div
                    style={{
                      padding: '20px',
                      textAlign: 'center',
                      color: '#71717a',
                    }}
                  >
                    No logs to display
                  </div>
                ) : (
                  filteredLogs.map((log) => (
                    <div
                      key={log.id}
                      style={{
                        padding: '4px 16px',
                        borderBottom: '1px solid rgba(63, 63, 70, 0.1)',
                        display: 'flex',
                        gap: '12px',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          'rgba(63, 63, 70, 0.2)')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = 'transparent')
                      }
                    >
                      <span
                        style={{
                          color: '#71717a',
                          flexShrink: 0,
                          width: '70px',
                        }}
                      >
                        [{log.timestamp}]
                      </span>
                      <span
                        style={{
                          color: getLogColor(log.type),
                          fontWeight: 600,
                          flexShrink: 0,
                          width: '45px',
                        }}
                      >
                        {log.type.toUpperCase()}
                      </span>
                      <pre
                        style={{
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          color: '#e4e4e7',
                        }}
                      >
                        {log.content}
                      </pre>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            /* Network Log List */
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
              }}
            >
              {networkLogs.length === 0 ? (
                <div
                  style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#71717a',
                  }}
                >
                  No network activity recorded
                </div>
              ) : (
                networkLogs.map((log) => (
                  <div
                    key={log.id}
                    style={{
                      borderBottom: '1px solid rgba(63, 63, 70, 0.3)',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedNetworkLog(
                          expandedNetworkLog === log.id ? null : log.id,
                        )
                      }
                      style={{
                        padding: '8px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        background:
                          expandedNetworkLog === log.id
                            ? 'rgba(63, 63, 70, 0.4)'
                            : 'transparent',
                        transition: 'background 0.2s',
                        width: '100%',
                        border: 'none',
                        borderBottom: '1px solid rgba(63, 63, 70, 0.1)',
                        textAlign: 'left',
                        font: 'inherit',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          flex: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <span
                          style={{
                            color:
                              log.status === 'failed'
                                ? '#ef4444'
                                : log.status >= 400
                                  ? '#f59e0b'
                                  : '#10b981',
                            fontWeight: 'bold',
                            width: '35px',
                          }}
                        >
                          {log.status}
                        </span>
                        <span
                          style={{
                            color: '#60a5fa',
                            fontWeight: 'bold',
                            width: '50px',
                          }}
                        >
                          {log.method}
                        </span>
                        <span
                          style={{
                            color: '#e4e4e7',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {String(log.url).split('/').pop() || '/'}
                        </span>
                      </div>
                      <span style={{ color: '#71717a', fontSize: '10px' }}>
                        {log.duration}
                      </span>
                    </button>

                    {expandedNetworkLog === log.id && (
                      <div
                        style={{
                          padding: '12px 16px',
                          background: 'rgba(24, 24, 27, 0.5)',
                          borderTop: '1px solid rgba(63, 63, 70, 0.2)',
                          fontSize: '11px',
                          color: '#d4d4d8',
                        }}
                      >
                        <div style={{ marginBottom: '8px' }}>
                          <span
                            style={{
                              color: '#71717a',
                              display: 'block',
                              marginBottom: '2px',
                            }}
                          >
                            Full URL:
                          </span>
                          <span style={{ wordBreak: 'break-all' }}>
                            {log.url}
                          </span>
                        </div>
                        {log.requestBody && (
                          <div style={{ marginBottom: '8px' }}>
                            <span
                              style={{
                                color: '#71717a',
                                display: 'block',
                                marginBottom: '2px',
                              }}
                            >
                              Request Body:
                            </span>
                            <pre
                              style={{
                                margin: 0,
                                padding: '8px',
                                background: 'rgba(0,0,0,0.3)',
                                borderRadius: '4px',
                                overflow: 'auto',
                                maxHeight: isFullScreen ? '600px' : '150px',
                              }}
                            >
                              {typeof log.requestBody === 'string'
                                ? log.requestBody
                                : JSON.stringify(log.requestBody, null, 2)}
                            </pre>
                          </div>
                        )}
                        {log.responseBody && (
                          <div style={{ marginBottom: '8px' }}>
                            <span
                              style={{
                                color: '#71717a',
                                display: 'block',
                                marginBottom: '2px',
                              }}
                            >
                              Response Body:
                            </span>
                            <pre
                              style={{
                                margin: 0,
                                padding: '8px',
                                background: 'rgba(0,0,0,0.3)',
                                borderRadius: '4px',
                                overflow: 'auto',
                                maxHeight: isFullScreen ? '800px' : '150px',
                              }}
                            >
                              {typeof log.responseBody === 'string'
                                ? log.responseBody
                                : JSON.stringify(log.responseBody, null, 2)}
                            </pre>
                          </div>
                        )}
                        {log.error && (
                          <div style={{ color: '#ef4444' }}>
                            <span
                              style={{ display: 'block', marginBottom: '2px' }}
                            >
                              Error:
                            </span>
                            {log.error instanceof Error
                              ? log.error.message
                              : String(log.error)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .debug-overlay-container ::-webkit-scrollbar {
          width: 6px;
        }
        .debug-overlay-container ::-webkit-scrollbar-track {
          background: transparent;
        }
        .debug-overlay-container ::-webkit-scrollbar-thumb {
          background: rgba(63, 63, 70, 0.5);
          border-radius: 3px;
        }
        .debug-overlay-container ::-webkit-scrollbar-thumb:hover {
          background: rgba(82, 82, 91, 0.8);
        }
      `}</style>
    </div>
  );
};

const iconButtonStyle = {
  background: 'transparent',
  border: 'none',
  color: '#a1a1aa',
  cursor: 'pointer',
  padding: '4px',
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.2s',
};

export default DebugOverlay;
